<script setup lang="ts">
import { AFFILIATE_BANNER_LOCALES, AFFILIATE_BANNERS, AFFILIATE_EMBED_PREFIX, AFFILIATE_PAGE_PREFIX, AFFILIATE_VIDEOS, type AffiliateBanner, type AffiliateBannerLocale, affiliateBannerPath, affiliateVideoCut } from '~/constants/affiliate'

/**
 * Ready-made promotion for partners: banners with embed code that already
 * carries their link (and a source tag per banner, so the statistics show
 * which one was clicked), short videos for TikTok, Reels and Shorts with a
 * caption each, a widget for their site, ready-to-post texts and the logo. Every
 * link leads to the partner's own page, which shows their name and the
 * discount and has the CV builder on it.
 */
const props = defineProps<{
  code: string
  /** The partner's own discount code, if they made one: the video's caption names it. */
  discountCode?: string | null
  discountPercent?: number | null
}>()

const { t, locale } = useI18n()
const { show: showSnackbar } = useSnackbar()
const { absUrl } = useSiteUrl()
const localize = useLocalizedPath()

/** The partner's page in a language (the dashboard's own by default), tagged with `source`. */
const link = (source?: string, lang?: AffiliateBannerLocale) =>
  `${absUrl(localize(`${AFFILIATE_PAGE_PREFIX}${props.code}`, lang))}${source ? `?src=${source}` : ''}`

// ---- Banners, in the language of the partner's audience ----
const LANG_NAMES: Record<AffiliateBannerLocale, string> = { en: 'English', cs: 'Čeština', de: 'Deutsch' }
/** The image's alt text on the partner's site, in the banner's language. */
const BANNER_ALT: Record<AffiliateBannerLocale, string> = {
  en: 'InMyResume: build a job-ready CV in minutes',
  cs: 'InMyResume: životopis hotový za pár minut',
  de: 'InMyResume: Ihr Lebenslauf in Minuten fertig',
}
const bannerLang = ref<AffiliateBannerLocale>((AFFILIATE_BANNER_LOCALES as readonly string[]).includes(locale.value) ? locale.value as AffiliateBannerLocale : 'en')
const bannerUrl = (banner: AffiliateBanner, retina = false) => absUrl(affiliateBannerPath(banner, bannerLang.value, retina)) ?? ''
const bannerLink = (banner: AffiliateBanner) => link(banner.id, bannerLang.value)

function embedCode(banner: AffiliateBanner): string {
  return `<a href="${bannerLink(banner)}" target="_blank" rel="sponsored noopener"><img src="${bannerUrl(banner)}" srcset="${bannerUrl(banner, true)} 2x" width="${banner.width}" height="${banner.height}" alt="${BANNER_ALT[bannerLang.value]}"></a>`
}

/** Posts and stories are downloaded and shared with a link; the rest are embedded. */
const isSocial = (banner: AffiliateBanner) => banner.layout === 'social' || banner.layout === 'story'
const formats = computed(() => [
  { id: 'web', label: t('affiliate.materials.formats.web'), banners: AFFILIATE_BANNERS.filter(b => !isSocial(b)) },
  { id: 'social', label: t('affiliate.materials.formats.social'), banners: AFFILIATE_BANNERS.filter(isSocial) },
])
const activeFormat = ref('web')
const banners = computed(() => formats.value.find(f => f.id === activeFormat.value)?.banners ?? [])

/** Previews are shrunk to fit their card, never enlarged. */
const PREVIEW_MAX = { width: 336, height: 280 }
function previewStyle(banner: AffiliateBanner) {
  const scale = Math.min(1, PREVIEW_MAX.width / banner.width, PREVIEW_MAX.height / banner.height)
  return { width: `${Math.round(banner.width * scale)}px`, height: `${Math.round(banner.height * scale)}px` }
}

const texts = computed(() => (['social', 'linkedin', 'email', 'short', 'video', 'story', 'coach', 'students'] as const).map(id => ({
  id,
  title: t(`affiliate.materials.texts.${id}.title`),
  body: t(`affiliate.materials.texts.${id}.body`, { link: link(`text-${id}`) }),
})))

/**
 * The caption carries the link with the video's own source tag and, when the
 * partner has one, their discount code: the video ends on our bare domain, and
 * whoever types it in still counts for the partner through the code. The
 * feature videos come in each banner language; the link then leads to the
 * partner's page in that language too.
 */
const videoLang = ref<AffiliateBannerLocale>(bannerLang.value)
const videos = computed(() => AFFILIATE_VIDEOS.map((video) => {
  const cut = affiliateVideoCut(video, videoLang.value)
  const caption = t(`affiliate.materials.video.items.${video.key}.caption`, { link: link(cut.source, cut.lang) })
  const code = props.discountCode && props.discountPercent
    ? `\n${t('affiliate.materials.video.captionCode', { code: props.discountCode, percent: props.discountPercent })}`
    : ''
  return {
    id: video.id,
    width: video.width,
    height: video.height,
    ...cut,
    title: t(`affiliate.materials.video.items.${video.key}.title`),
    text: t(`affiliate.materials.video.items.${video.key}.text`),
    caption: `${caption}${code}\n\n${t('affiliate.materials.video.captionTags')}`,
  }
}))

// ---- Widget: an iframe for the partner's site (pages/embed/[code].vue) ----
const WIDGET_SIZES = [
  { id: 'wide', width: '100%', height: 260 },
  { id: 'box', width: '340', height: 420 },
] as const
const widgetSize = ref<typeof WIDGET_SIZES[number]['id']>('wide')
const widgetDark = ref(false)
const widgetSrc = computed(() => `${absUrl(localize(`${AFFILIATE_EMBED_PREFIX}${props.code}`))}${widgetDark.value ? '?theme=dark' : ''}`)
const widget = computed(() => WIDGET_SIZES.find(size => size.id === widgetSize.value)!)
const widgetCode = computed(() => `<iframe src="${widgetSrc.value}" width="${widget.value.width}" height="${widget.value.height}" style="border:0;max-width:100%" loading="lazy" title="InMyResume"></iframe>`)

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    showSnackbar(t('affiliate.materials.copied'))
  } catch {
    // Clipboard blocked: the text stays selectable by hand.
  }
}
</script>

<template>
  <section>
    <h2 class="!text-xl">{{ t('affiliate.materials.title') }}</h2>
    <p class="mt-2 text-sm text-muted dark:text-gray-400">{{ t('affiliate.materials.subtitle') }}</p>

    <!-- Banners -->
    <div class="mt-6 flex flex-wrap items-center justify-between gap-4">
      <h3 class="font-title !text-base font-semibold text-ink dark:text-white">{{ t('affiliate.materials.banners') }}</h3>
      <div class="flex flex-wrap items-center gap-3">
        <select v-model="bannerLang" class="field-select !w-40" :aria-label="t('affiliate.materials.bannerLanguage')">
          <option v-for="code in AFFILIATE_BANNER_LOCALES" :key="code" :value="code">{{ LANG_NAMES[code] }}</option>
        </select>
        <div class="inline-flex rounded-full border border-line p-1 dark:border-gray-700" role="group">
          <button
            v-for="format in formats"
            :key="format.id"
            type="button"
            class="whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-300"
            :class="activeFormat === format.id ? 'bg-ink text-white dark:bg-white dark:text-ink' : 'text-muted hover:text-ink dark:text-gray-400 dark:hover:text-white'"
            :aria-pressed="activeFormat === format.id"
            @click="activeFormat = format.id"
          >
            {{ format.label }}
          </button>
        </div>
      </div>
    </div>

    <div class="mt-4 grid gap-4 md:grid-cols-2">
      <div v-for="banner in banners" :key="banner.id" class="flex flex-col rounded-[0.625rem] border border-line p-4 dark:border-gray-700">
        <div class="flex min-h-72 items-center justify-center rounded-lg bg-surface p-4 dark:bg-gray-800">
          <img
            :src="affiliateBannerPath(banner, bannerLang)"
            :srcset="`${affiliateBannerPath(banner, bannerLang, true)} 2x`"
            :width="banner.width"
            :height="banner.height"
            :style="previewStyle(banner)"
            :alt="t('affiliate.materials.bannerAlt', { width: banner.width, height: banner.height })"
            loading="lazy"
            class="max-w-full"
          >
        </div>
        <div class="mt-3 flex items-center justify-between gap-3">
          <span class="font-title text-sm font-semibold text-ink dark:text-white">{{ banner.width }} × {{ banner.height }}</span>
          <div class="flex gap-2">
            <a
              :href="affiliateBannerPath(banner, bannerLang, true)"
              :download="`inmyresume-${banner.id}-${bannerLang}.png`"
              class="btn btn-outline btn-sm"
            >
              <i class="bi bi-download" aria-hidden="true" />
              {{ t('affiliate.materials.download') }}
            </a>
            <button v-if="!isSocial(banner)" type="button" class="btn btn-dark btn-sm" @click="copy(embedCode(banner))">
              <i class="bi bi-code-slash" aria-hidden="true" />
              {{ t('affiliate.materials.copyCode') }}
            </button>
            <button v-else type="button" class="btn btn-dark btn-sm" @click="copy(bannerLink(banner))">
              <i class="bi bi-link-45deg" aria-hidden="true" />
              {{ t('affiliate.materials.copyLink') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <p class="mt-2 text-xs text-muted dark:text-gray-400">{{ t('affiliate.materials.bannersHint') }}</p>

    <!-- Video -->
    <div class="mt-10 flex flex-wrap items-center justify-between gap-4">
      <h3 class="font-title !text-base font-semibold text-ink dark:text-white">{{ t('affiliate.materials.video.title') }}</h3>
      <select v-model="videoLang" class="field-select !w-40" :aria-label="t('affiliate.materials.video.language')">
        <option v-for="code in AFFILIATE_BANNER_LOCALES" :key="code" :value="code">{{ LANG_NAMES[code] }}</option>
      </select>
    </div>
    <p class="mt-1 text-sm text-muted dark:text-gray-400">{{ t('affiliate.materials.video.text') }}</p>
    <div class="mt-4 grid gap-4 xl:grid-cols-2">
      <div
        v-for="video in videos"
        :key="video.src"
        class="flex flex-col gap-5 rounded-[0.625rem] border border-line p-4 sm:flex-row dark:border-gray-700"
      >
        <video
          :src="`${video.src}#t=0.1`"
          :width="video.width"
          :height="video.height"
          class="aspect-[9/16] w-full max-w-[220px] self-center rounded-lg bg-black sm:self-start"
          controls
          muted
          playsinline
          preload="metadata"
        />
        <div class="flex min-w-0 flex-1 flex-col">
          <span class="font-title text-sm font-semibold text-ink dark:text-white">{{ video.title }}</span>
          <span class="mt-1 text-sm text-body dark:text-gray-300">{{ video.text }}</span>
          <span class="mt-1 text-xs text-muted dark:text-gray-400">
            {{ t('affiliate.materials.video.format', { width: video.width, height: video.height, seconds: video.seconds }) }}
          </span>
          <span class="mt-3 text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400">{{ t('affiliate.materials.video.captionTitle') }}</span>
          <p class="mt-1 whitespace-pre-line break-words rounded-lg bg-surface p-3 text-sm text-body dark:bg-gray-800 dark:text-gray-300">{{ video.caption }}</p>
          <div class="mt-3 flex flex-wrap gap-2">
            <a :href="video.src" :download="video.file" class="btn btn-dark btn-sm">
              <i class="bi bi-download" aria-hidden="true" />
              {{ t('affiliate.materials.video.download') }}
            </a>
            <button type="button" class="btn btn-outline btn-sm" @click="copy(video.caption)">
              <i class="bi bi-clipboard" aria-hidden="true" />
              {{ t('affiliate.materials.video.copyCaption') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <p class="mt-2 text-xs text-muted dark:text-gray-400">{{ t('affiliate.materials.video.hint') }}</p>

    <!-- Widget -->
    <h3 class="mt-10 font-title !text-base font-semibold text-ink dark:text-white">{{ t('affiliate.widget.sectionTitle') }}</h3>
    <p class="mt-1 text-sm text-muted dark:text-gray-400">{{ t('affiliate.widget.sectionText') }}</p>
    <div class="mt-4 rounded-[0.625rem] border border-line p-4 dark:border-gray-700">
      <div class="flex flex-wrap items-center gap-3">
        <div class="inline-flex rounded-full border border-line p-1 dark:border-gray-700" role="group">
          <button
            v-for="size in WIDGET_SIZES"
            :key="size.id"
            type="button"
            class="whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors duration-300"
            :class="widgetSize === size.id ? 'bg-ink text-white dark:bg-white dark:text-ink' : 'text-muted hover:text-ink dark:text-gray-400 dark:hover:text-white'"
            :aria-pressed="widgetSize === size.id"
            @click="widgetSize = size.id"
          >
            {{ t(`affiliate.widget.sizes.${size.id}`) }}
          </button>
        </div>
        <label class="flex cursor-pointer items-center gap-2 text-sm">
          <input v-model="widgetDark" type="checkbox" class="field-check">
          {{ t('affiliate.widget.darkTheme') }}
        </label>
        <button type="button" class="btn btn-dark btn-sm ml-auto" @click="copy(widgetCode)">
          <i class="bi bi-code-slash" aria-hidden="true" />
          {{ t('affiliate.materials.copyCode') }}
        </button>
      </div>
      <div class="mt-4 flex justify-center rounded-lg p-4" :class="widgetDark ? 'bg-gray-900' : 'bg-surface dark:bg-gray-800'">
        <iframe
          :key="widgetSrc"
          :src="widgetSrc"
          :width="widget.width"
          :height="widget.height"
          class="max-w-full border-0"
          loading="lazy"
          :title="t('affiliate.widget.previewTitle')"
        />
      </div>
      <p class="mt-2 text-xs text-muted dark:text-gray-400">{{ t('affiliate.widget.hint') }}</p>
    </div>

    <!-- Texts -->
    <h3 class="mt-10 font-title !text-base font-semibold text-ink dark:text-white">{{ t('affiliate.materials.textsTitle') }}</h3>
    <div class="mt-4 grid gap-4 md:grid-cols-2">
      <div v-for="text in texts" :key="text.id" class="flex flex-col rounded-[0.625rem] border border-line p-4 dark:border-gray-700">
        <div class="flex items-center justify-between gap-3">
          <span class="font-title text-sm font-semibold text-ink dark:text-white">{{ text.title }}</span>
          <button type="button" class="btn btn-outline btn-sm" @click="copy(text.body)">
            <i class="bi bi-clipboard" aria-hidden="true" />
            {{ t('affiliate.materials.copy') }}
          </button>
        </div>
        <p class="mt-3 whitespace-pre-line break-words text-sm text-body dark:text-gray-300">{{ text.body }}</p>
      </div>
    </div>

    <!-- Logo -->
    <div class="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-[0.625rem] border border-line p-4 dark:border-gray-700">
      <div class="flex items-center gap-4">
        <img src="/logo.svg" alt="InMyResume" width="160" height="34" class="h-8 w-auto dark:hidden">
        <img src="/logo_white.svg" alt="InMyResume" width="160" height="34" class="hidden h-8 w-auto dark:block">
        <span class="text-sm text-muted dark:text-gray-400">{{ t('affiliate.materials.logoHint') }}</span>
      </div>
      <div class="flex gap-2">
        <a href="/logo.svg" download="inmyresume-logo.svg" class="btn btn-outline btn-sm">
          <i class="bi bi-download" aria-hidden="true" />
          {{ t('affiliate.materials.logoDark') }}
        </a>
        <a href="/logo_white.svg" download="inmyresume-logo-white.svg" class="btn btn-outline btn-sm">
          <i class="bi bi-download" aria-hidden="true" />
          {{ t('affiliate.materials.logoLight') }}
        </a>
      </div>
    </div>
  </section>
</template>
